import misago from "misago/index"

const SVG_WIDTH = 320
const SVG_HEIGHT = 72
const SVG_PADDING = 6

function formatNumber(value) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatChange(value) {
  const sign = value > 0 ? "+" : ""
  return sign + formatNumber(value)
}

function formatPercent(value) {
  const sign = value > 0 ? "+" : ""
  return sign + formatNumber(value) + "%"
}

function formatTime(timestamp) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/New_York",
  }).format(new Date(timestamp))
}

function getSparklinePath(points) {
  if (!points.length) {
    return ""
  }

  const prices = points.map((point) => point.price)
  const minPrice = Math.min(...prices)
  const maxPrice = Math.max(...prices)
  const priceRange = maxPrice - minPrice || 1
  const xStep =
    points.length > 1
      ? (SVG_WIDTH - SVG_PADDING * 2) / (points.length - 1)
      : 0

  return points
    .map((point, index) => {
      const x = SVG_PADDING + index * xStep
      const y =
        SVG_HEIGHT -
        SVG_PADDING -
        ((point.price - minPrice) / priceRange) * (SVG_HEIGHT - SVG_PADDING * 2)

      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(" ")
}

function renderLoading(element) {
  element.innerHTML = `
    <div class="market-indices-panel" aria-label="${gettext("Loading market data")}">
      <div class="market-indices-card-placeholder" aria-hidden="true">
        <div class="market-indices-card-placeholder-bar is-short"></div>
        <div class="market-indices-card-placeholder-bar is-wide"></div>
        <div class="market-indices-card-placeholder-bar is-medium"></div>
        <div class="market-indices-card-placeholder-chart"></div>
      </div>
      <div class="market-indices-card-placeholder" aria-hidden="true">
        <div class="market-indices-card-placeholder-bar is-short"></div>
        <div class="market-indices-card-placeholder-bar is-wide"></div>
        <div class="market-indices-card-placeholder-bar is-medium"></div>
        <div class="market-indices-card-placeholder-chart"></div>
      </div>
    </div>
  `
}

function renderEmpty(element) {
  element.innerHTML = `
    <div class="market-indices-widget-empty">
      ${gettext("Market data is temporarily unavailable.")}
    </div>
  `
}

function renderMarketCard(item) {
  const isUp = item.change >= 0
  const lastPoint = item.points[item.points.length - 1]
  const changeClass = isUp ? "is-up" : "is-down"
  const path = getSparklinePath(item.points)

  return `
    <article class="market-indices-card">
      <div class="market-indices-card-header">
        <div class="market-indices-card-title">${item.shortName}</div>
        <div class="market-indices-card-time">${formatTime(lastPoint.timestamp)} ET</div>
      </div>
      <div class="market-indices-card-price">${formatNumber(item.regularMarketPrice)}</div>
      <div class="market-indices-card-change ${changeClass}">
        ${formatChange(item.change)} (${formatPercent(item.changePercent)})
      </div>
      <svg
        class="market-indices-sparkline"
        viewBox="0 0 ${SVG_WIDTH} ${SVG_HEIGHT}"
        role="img"
        aria-label="${item.shortName} ${gettext("intraday line chart")}"
        preserveAspectRatio="none"
      >
        <path
          class="market-indices-sparkline-track"
          d="M${SVG_PADDING} ${(SVG_HEIGHT / 2).toFixed(2)} L${(
            SVG_WIDTH - SVG_PADDING
          ).toFixed(2)} ${(SVG_HEIGHT / 2).toFixed(2)}"
        ></path>
        <path class="market-indices-sparkline-line ${changeClass}" d="${path}"></path>
      </svg>
    </article>
  `
}

function renderData(element, payload) {
  if (!payload.indices || !payload.indices.length) {
    renderEmpty(element)
    return
  }

  const updatedAt = payload.updatedAt ? formatTime(payload.updatedAt) : null

  element.innerHTML = `
    <div class="market-indices-panel">
      ${payload.indices.map(renderMarketCard).join("")}
      ${
        updatedAt
          ? `<div class="market-indices-widget-caption">${gettext(
              "Updated"
            )} ${updatedAt} ET</div>`
          : ""
      }
    </div>
  `
}

function renderQuoteData(element, payload) {
  if (!payload.quote) {
    renderEmpty(element)
    return
  }

  const updatedAt = payload.updatedAt ? formatTime(payload.updatedAt) : null

  element.innerHTML = `
    <div class="market-indices-panel">
      ${renderMarketCard(payload.quote)}
      ${
        updatedAt
          ? `<div class="market-indices-widget-caption">${gettext(
              "Updated"
            )} ${updatedAt} ET</div>`
          : ""
      }
    </div>
  `
}

async function loadMarketWidget(element) {
  const url = element.getAttribute("data-url")
  const widgetType = element.getAttribute("data-market-widget") || "indices"
  if (!url) {
    return
  }

  renderLoading(element)

  try {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()

    if (widgetType === "quote") {
      renderQuoteData(element, payload)
    } else {
      renderData(element, payload)
    }
  } catch (error) {
    renderEmpty(element)
  }
}

export default function initializer() {
  const elements = document.querySelectorAll("[data-market-widget]")

  elements.forEach((element) => {
    if (element.getAttribute("data-loaded") === "true") {
      return
    }

    element.setAttribute("data-loaded", "true")
    loadMarketWidget(element)
  })
}

misago.addInitializer({
  name: "market-indices",
  initializer: initializer,
  before: "store",
})

